import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { boardsService } from '../api/boards.service';
import { BOARD_KEYS } from './queryKeys';
import type { CreateBoardPayload, UpdateBoardPayload, ReorderPayload } from '../types';

export function useCreateBoard(projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (data: CreateBoardPayload) => boardsService.create(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.byProject(orgId ?? '', projectId),
      });
    },
  });
}

export function useUpdateBoard(projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: ({ boardId, data }: { boardId: string; data: UpdateBoardPayload }) =>
      boardsService.update(boardId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.byProject(orgId ?? '', projectId),
      });
    },
  });
}

export function useDeleteBoard(projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (boardId: string) => boardsService.delete(boardId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.byProject(orgId ?? '', projectId),
      });
    },
  });
}

export function useReorderBoards(projectId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (data: ReorderPayload) => boardsService.reorder(projectId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: BOARD_KEYS.byProject(orgId ?? '', projectId),
      });
    },
  });
}
